import React, { useEffect, useState } from 'react'
import { MeetingTemplatePreset } from '../../../store/ai/meetingTemplate'
import { Col, Container, Form, Row, Spinner } from 'react-bootstrap'
import { Modal } from '../../../components/Modal/Modal'
import { useGetFeaturesQuery } from '../../../store/ai/ai'
import { useAppSelector } from '../../../store/hooks'
import { selectAccountId } from '../../../store/auth/authSlice'
import classNames from 'classnames'
import { MissingScopeAction } from '../../../components/ScopedBlock/models'
import { ScopedBlock } from '../../../components/ScopedBlock/ScopedBlock'
import { useGetAllScorecardsQuery } from '../../../store/ai/scorecard'
import { AiFeature } from '../../../models/AiFeature'
import { useZocksQuota } from '../../../components/ScopedBlock/hooks'

export interface AddMeetingTemplateFormModalProps {
  show: boolean
  onSave: (addedForms: MeetingTemplatePreset[], removedFormIds: string[]) => void
  onCancel: () => void
  currentForms: AiFeature[]
}

export const AddMeetingTemplateFormModal: React.FC<AddMeetingTemplateFormModalProps> = ({ show, onSave, onCancel, currentForms }) => {
  const accountId = useAppSelector(selectAccountId)
  const [newForms, setNewForms] = useState<MeetingTemplatePreset[]>([])
  const [deletedFormIds, setDeletedFormIds] = useState<string[]>([])
  const [formIds, setFormIds] = useState('')
  const { getQuotaValue } = useZocksQuota()
  const maxFormsInMeetingTemplate = getQuotaValue('ai.maxFormsInMeetingTemplate.value') ?? 0
  const currentFormIds = currentForms.map(({ id }) => {
    return id
  })
  const activeFormCount = currentFormIds.length + newForms.length - deletedFormIds.length
  const canAddForm = activeFormCount < maxFormsInMeetingTemplate

  const { data: featureData, isFetching: isFetchingFeatureData } = useGetFeaturesQuery({
    accountId: accountId,
    pageRequest: { page: 0, size: 100 },
    type: 'scorecard',
    includeRestricted: true,
  })

  const { data: scorecardData, isFetching: isScorecardFetching } = useGetAllScorecardsQuery({
    accountId: accountId,
    aiFeatureIds: formIds,
  })

  useEffect(() => {
    if (featureData) {
      const formIds = featureData?.data.map((feature) => feature.id).join(',')
      setFormIds(formIds)
    }
  }, [featureData])

  const handleAddForm = (addedFormId: string, presetId: string) => {
    if (currentFormIds.includes(addedFormId)) {
      setDeletedFormIds((deletedFormIds) => deletedFormIds.filter((formId) => formId !== addedFormId))
      return
    }
    setNewForms((presetsToAdd) => [...presetsToAdd, { aiFeaturePresetId: presetId, aiFeatureId: addedFormId, metadata: '' }])
  }

  const handleRemoveForm = (removedFormId: string) => {
    setNewForms((presetsToAdd) => presetsToAdd.filter((preset) => preset.aiFeatureId !== removedFormId))
    if (currentFormIds.includes(removedFormId)) {
      setDeletedFormIds((deletedFormIds) => [...new Set([...deletedFormIds, removedFormId])])
    }
  }

  const handleSave = () => {
    onSave(newForms, deletedFormIds)
  }

  const getBody = () => {
    if (!featureData || isFetchingFeatureData || isScorecardFetching) {
      return (
        <div className='d-flex align-items-center justify-content-center'>
          <Spinner />
        </div>
      )
    }
    return (
      <Container>
        <Row className='pb-3'>
          <Col>
            <h4 className='d-flex justify-content-center align-items-center'>{`Manage forms (${activeFormCount}/${maxFormsInMeetingTemplate})`}</h4>
          </Col>
        </Row>
        <Row className='pb-4'>
          <Col className='p-0'>
            {featureData.data
              .filter((feature) =>
                scorecardData ? scorecardData.data.some((scorecard) => scorecard.aiFeatureIds[0] === feature.id && scorecard.type === 'form') : false,
              )
              .map(({ id, name, preset }) => {
                if (!preset) {
                  return null
                }
                const formAdded =
                  newForms.some((p) => p.aiFeatureId === id) ||
                  (currentFormIds.some((currentFormId) => currentFormId === id) && !deletedFormIds.some((deletedFormId) => deletedFormId === id))
                const formName = (scorecardData && scorecardData?.data.find((scorecard) => scorecard.aiFeatureIds[0] === id)?.name) ?? name
                return (
                  <div key={preset.id} className={classNames('d-flex gap-3 p-3 border-top align-items-center checkbox-container', { addedPreset: formAdded })}>
                    <ScopedBlock
                      authActions={[() => canAddForm || formAdded]}
                      missingScopeAction={MissingScopeAction.UPGRADE}
                      showUpgradeBadge={false}
                      tooltipText={<b className='text-wrap'>Your can have maximum {maxFormsInMeetingTemplate} forms in a meeting type.</b>}
                      tooltipButtonText={'Upgrade'}
                    >
                      <Form.Check
                        className='zocks-border-color-primary'
                        type='checkbox'
                        checked={formAdded}
                        disabled={!canAddForm && !formAdded}
                        onChange={formAdded ? () => handleRemoveForm(id) : () => handleAddForm(id, preset.id)}
                      />
                    </ScopedBlock>
                    <div className='lead-small'>{formName}</div>
                  </div>
                )
              })}
          </Col>
        </Row>
      </Container>
    )
  }
  return (
    <Modal
      className={'h-75'}
      show={show}
      onHide={onCancel}
      header={<></>}
      body={getBody()}
      showFooterActions={true}
      onFooterPrimaryAction={handleSave}
      footerPrimaryActionTitle={'Change forms'}
      scrollable
      size='lg'
    />
  )
}
